import {
    Archive,
    Bell,
    BellDot,
    Cog,
    LayoutGrid,
    LayoutList,
    Menu,
    NotepadText,
    Tag,
    Trash2,
    TriangleAlert,
    X,
} from "lucide-react";
import UserAvatar from "../../components/generic/UserAvatar";
import { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../contexts/auth.context";
import LoadingPage from "../../components/generic/LoadingPage";
import { clamp01, cn } from "../../util/base.util";
import NotesView from "./components/NotesView";
import { SidebarContext } from "../../contexts/sidebar.context";
import { ToasterContext } from "../../contexts/toaster.context";
import SettingsModal from "./components/SettingsModal";
import Modal from "../../components/generic/Modal";
import { useApi } from "../../hooks/api.hook";
import VerifyEmailModal from "../../components/VerifyEmailModal";
import NewItemModal from "./components/NewItemModal";

export default function HomePage() {
    const auth = useContext(AuthContext);
    const sidebar = useContext(SidebarContext);
    const toaster = useContext(ToasterContext);
    const authService = useApi("auth");
    const [layoutStyle, setLayoutStyle] = useState<"grid" | "list">("grid");
    const [search, setSearch] = useState("");
    const [emailVerifyWarn, setEmailVerifyWarn] = useState(false);
    const [emailVerifyOpen, setEmailVerifyOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);

    const [scrollT, setScrollT] = useState(1);
    const maxScroll = 184;
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!scrollRef.current) return;

        const el = scrollRef.current;

        const cb = () => {
            const box = el.getBoundingClientRect();
            setScrollT(clamp01(box.top / maxScroll));
        };

        window.addEventListener("scroll", cb);

        return () => {
            window.removeEventListener("scroll", cb);
        };
    }, [scrollRef.current]);

    useEffect(() => {
        setEmailVerifyWarn(
            auth.user !== undefined && auth.user.emailVerified === null
        );
    }, [auth.user]);

    if (!auth.user) {
        return <LoadingPage />;
    }

    return (
        <div>
            {emailVerifyWarn && (
                <div className="bg-warning text-warning-content">
                    <div className="mx-auto max-w-screen-md">
                        <div className="flex items-center p-2 gap-2">
                            <div>
                                <TriangleAlert />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs">
                                    You have not yet verified your email address. Email
                                    verification is required to enable certain settings like 2FA
                                    and password resets
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center justify-end">
                            <button onClick={() => setEmailVerifyOpen(true)} className="btn btn-xs btn-ghost hover:bg-[#ffffff33] border-none hover:text-warning-content">
                                Verify email
                            </button>
                            <button
                                onClick={() => setEmailVerifyWarn(false)}
                                className="btn btn-xs btn-ghost hover:bg-[#ffffff33] border-none hover:text-warning-content"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="mx-auto max-w-screen-md">
                <div
                    className="flex items-center justify-center"
                    style={{
                        height: maxScroll,
                        opacity: clamp01(scrollT*2 - 1),
                    }}
                >
                    <div
                        className="text-center relative"
                        style={{
                            top: (maxScroll / 2) * (1 - scrollT),
                        }}
                    >
                        <div className="avatar">
                            <div className="w-16">
                                <img src="/logo192.png" alt="Logo" />
                            </div>
                        </div>
                        <p className="mt-2 text-2xl">Lister</p>
                    </div>
                </div>
                <div ref={scrollRef} className="Searchbar p-4 sticky top-0 z-[1]">
                    <div className="bg-base-100 flex items-center rounded-full shadow-lg">
                        <div>
                            <label
                                onClick={() => sidebar.setOpen(true)}
                                className="btn btn-ghost rounded-l-full"
                            >
                                <Menu size={16} />
                            </label>
                        </div>
                        <div className="flex-1 flex items-center">
                            <div className="flex-1">
                                <input
                                    type="text"
                                    placeholder="Search your notes"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="w-full bg-transparent border-none outline-none"
                                />
                            </div>
                            <div>
                                {search !== "" && (
                                    <button
                                        onClick={() => setSearch("")}
                                        className="btn btn-sm btn-ghost"
                                    >
                                        <X size={12} />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div>
                            <button
                                onClick={() =>
                                    setLayoutStyle((prev) => (prev === "grid" ? "list" : "grid"))
                                }
                                className="btn btn-ghost"
                            >
                                {layoutStyle === "grid" ? (
                                    <LayoutList size={16} />
                                ) : (
                                    <LayoutGrid size={16} />
                                )}
                            </button>
                        </div>
                        {/* <div className="dropdown dropdown-end">
                            <span tabIndex={0} className="btn btn-ghost">{toaster.notifications.length > 0 ? <BellDot size={16} /> : <Bell size={16} />}</span>
                            <div tabIndex={0} className="dropdown-content">
                                <div className="bg-base-200 w-screen max-w-[240px] rounded-md shadow-lg text-sm border border-base-100">
                                    <p className="p-2 text-xs opacity-50">Notifications</p>
                                    <div className="divider m-0"></div>
                                    <div className="h-64 overflow-auto">
                                        {toaster.notifications.map(v => (
                                            <div key={`notif-${v.id}`} className="p-2 border-b border-b-[#ffffff33] odd:bg-base-100">
                                                <div className="flex items-center">
                                                    <div className="flex-1">
                                                        <p className="text-[10px] opacity-50">{v.createdAt.toLocaleString()}</p>
                                                    </div>
                                                    <div>
                                                        <div onClick={() => toaster.removeNotification(v.id)} className="btn btn-xs">Clear</div>
                                                    </div>
                                                </div>
                                                {v.content}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="divider m-0"></div>
                                    <div className="p-2 flex justify-end items-center">
                                        <div onClick={() => toaster.clearNotifications()} className="btn btn-xs">Clear All</div>
                                    </div>
                                </div>
                            </div>
                        </div> */}
                        <button
                            onClick={() => setSettingsOpen(true)}
                            className="btn btn-ghost rounded-r-full"
                        >
                            <UserAvatar id={auth.user.id} className="w-6" />
                        </button>
                    </div>
                </div>

                <div className="p-2">
                    <NotesView layout={layoutStyle} search={search} />
                </div>

                {/* <div className={cn(
                    "sidebar fixed top-0 left-0 w-screen h-screen bg-[#00000088]",
                    sidebarOpen && 'open'
                )}>
                    <div
                    onClick={e => {
                        if (e.target === e.currentTarget) setSidebarOpen(false);
                    }}
                    className="sidebar-content">
                        <div className="h-screen overflow-auto bg-base-300 max-w-[300px] grid grid-rows-[auto_1fr] border-r border-r-[#ffffff33]">
                            <div className="flex items-center p-2">
                                <div className="flex-1 flex items-center gap-4">
                                    <div className="avatar"><div className="w-8"><img src="logo192.png" alt="Lister" /></div></div>
                                    <p>Lister</p>
                                </div>
                                <div>
                                    <button onClick={() => setSidebarOpen(false)} className="btn btn-sm btn-ghost"><X size={16} /></button>
                                </div>
                            </div>

                            <div className="overflow-auto p-2">
                                <ul className="menu w-full my-4 text-lg">
                                    <li><span className="rounded-full py-4 menu-active"><NotepadText size={18} /> Notes</span></li>
                                    <li><span className="rounded-full py-4"><Bell size={18} /> Reminders</span></li>
                                    <li><span className="rounded-full py-4"><Tag size={18} /> Edit labels</span></li>
                                    <li><span className="rounded-full py-4"><Archive size={18} /> Archive</span></li>
                                    <li><span className="rounded-full py-4"><Trash2 size={18} /> Trash</span></li>
                                    <li><span className="rounded-full py-4"><Cog size={18} /> Settings</span></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div> */}
            </div>

            <NewItemModal />

            <SettingsModal
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
            />

            {!auth.user.bSeenChangeLog && (
                <Modal id="changelog_modal" open title="Changelog" onClose={() => {
                    // console.log("close changelog modal");
                    if (!auth.user) return;
                    auth.setUser({...auth.user, bSeenChangeLog: true });
                }}>
                    <div className="max-h-[70vh] overflow-auto">
                        <section>
                            <h3 className="text-xl font-semibold text-accent">
                                Version 2.0 – Major Feature Update + UI Overhaul
                            </h3>
                            <p className="mt-2 opacity-70">
                                A significant update improving user experience, adding powerful
                                organization tools, and enhancing security.
                            </p>

                            <div className="mt-4">
                                <h4 className="font-semibold text-gray-200">
                                    🔒 Authentication & Account Management
                                </h4>
                                <ul className="list-disc list-inside mt-1 space-y-1 opacity-70">
                                    <li>Email verification</li>
                                    <li>Multi-factor authentication via email</li>
                                    <li>Password reset functionality</li>
                                </ul>
                            </div>

                            <div className="mt-4">
                                <h4 className="font-semibold text-gray-200">
                                    🧑‍💻 UI/UX Improvements
                                </h4>
                                <ul className="list-disc list-inside mt-1 space-y-1 opacity-70">
                                    <li>Full redesign of the interface for better usability</li>
                                    <li>New grid layout with improved flow and clarity</li>
                                    <li>
                                        Optional list or grid view for personalized organization
                                    </li>
                                    <li>More informative previews for lists and notes</li>
                                </ul>
                            </div>

                            <div className="mt-4">
                                <h4 className="font-semibold text-gray-200">
                                    🧠 New Note Features
                                </h4>
                                <ul className="list-disc list-inside mt-1 space-y-1 opacity-70">
                                    <li>Create freeform text notes</li>
                                    <li>Set reminders on notes and lists</li>
                                    <li>Add custom labels for sorting and filtering</li>
                                    <li>Archive items to declutter your active view</li>
                                    <li>Trash items to hide or recover them later</li>
                                    <li>Pin important notes/lists to keep them visible at the top</li>
                                    <li>See all collaborators on a list</li>
                                    <li>See who is currently viewing the same list as you</li>
                                </ul>
                            </div>

                            <div className="mt-4">
                                <h4 className="font-semibold text-gray-200">
                                    🔍 Filtering & Organization
                                </h4>
                                <ul className="list-disc list-inside mt-1 space-y-1 opacity-70">
                                    <li>
                                        Filter items by label, reminder, archived, and trashed
                                        status
                                    </li>
                                    <li>Toggle between grid and list view for lists and notes</li>
                                </ul>
                            </div>
                        </section>
                    </div>

                    <div className="modal-action">
                        <button onClick={async () => {
                            const res = await authService.patch("me", {
                                bSeenChangeLog: true
                            });

                            if (res.ok && res.data) {
                                auth.setUser(res.data);
                            }

                        }} className="btn btn-outline">Ok</button>
                    </div>
                </Modal>
            )}

            {auth.user.emailVerified === null && (
                <VerifyEmailModal id="verify_email_modal" open={emailVerifyOpen} onClose={() => setEmailVerifyOpen(false)} />
            )}
        </div>
    );
}
